import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import eventService from './eventService';
import { convertFirebaseDate } from '../../helpers/dateHelper';
import { setAlertError, setAlertInfo, setAlertSuccess } from '../alert/alertSlice';

export const getAllEvents = createAsyncThunk('event/getAllEvents', async (data, thunkAPI) => {
  return await eventService
    .get()
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

/**
 * Loads one page of the events list. The server filters by date scope and
 * sorts, so the page holds only the events the list is about to show.
 */
export const getEventPage = createAsyncThunk(
  'event/getEventPage',
  async (data = {}, thunkAPI) => {
    return await eventService.getPage(data).catch((error) => {
      const err = error.response?.data;
      return thunkAPI.rejectWithValue(err?.message || error.message);
    });
  }
);

/** The handful of events shown in the navigation menu. */
export const getOngoingEvents = createAsyncThunk(
  'event/getOngoingEvents',
  async (data = {}, thunkAPI) => {
    return await eventService.getPage({ scope: 'ongoing', limit: 5 }).catch((error) => {
      const err = error.response?.data;
      return thunkAPI.rejectWithValue(err?.message || error.message);
    });
  }
);

export const getAllEventsByVenue = createAsyncThunk(
  'event/getAllEventsByVenue',
  async (data, thunkAPI) => {
    return await eventService
      .getAllByVenue(data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const getEvent = createAsyncThunk('event/getEvent', async (data, thunkAPI) => {
  return await eventService
    .getById(data)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const getEventScoreMethod = createAsyncThunk(
  'event/getEventScoreMethod',
  async (data, thunkAPI) => {
    return await eventService
      .getEventScoreMethod(data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const createEvent = createAsyncThunk('event/createEvent', async (data, thunkAPI) => {
  return await eventService
    .create(data)
    .then((response) => {
      thunkAPI.dispatch(setAlertSuccess('Event created successfully'));
      console.log(response);
      thunkAPI.dispatch(updateCMEvent({ cmEventId: data.clubMasterId, eventId: response.id }));

      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const duplicateEvent = createAsyncThunk('event/duplicateEvent', async (data, thunkAPI) => {
  return await eventService
    .duplicate(data.eventId)
    .then((response) => {
      thunkAPI.dispatch(setAlertSuccess('Event duplicated successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const transferEvent = createAsyncThunk(
  'event/transferEvent',
  async ({ eventId, workspaceId }, thunkAPI) => {
    return await eventService
      .transfer(eventId, workspaceId)
      .then((response) => {
        thunkAPI.dispatch(setAlertSuccess('Event transferred successfully'));
        return response;
      })
      .catch((error) => {
        const err = error.response?.data;
        const message =
          typeof err === 'string' ? err : err?.message || 'Failed to transfer event.';
        thunkAPI.dispatch(setAlertError(message));
        return thunkAPI.rejectWithValue(message);
      });
  }
);

export const updateEvent = createAsyncThunk('event/updateEvent', async (data, thunkAPI) => {
  return await eventService
    .update(data.id, data)
    .then((response) => {
      thunkAPI.dispatch(setAlertSuccess('Event updated successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const updateEventTeam = createAsyncThunk('event/updateEventTeam', async (data, thunkAPI) => {
  return await eventService
    .updateTeam(data.eventId, data.team)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const deleteEvent = createAsyncThunk('event/deleteEvent', async (data, thunkAPI) => {
  return await eventService
    .remove(data)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const addEventGame = createAsyncThunk('event/addEventGame', async (data, thunkAPI) => {
  return await eventService
    .addGame(data.eventId, data.sectionId, data.game)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const getCMEvents = createAsyncThunk('event/getCMEvents', async (data, thunkAPI) => {
  return await eventService
    .getCMEvents(data.cmClubId)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const getCMEvent = createAsyncThunk('event/getCMEvent', async (data, thunkAPI) => {
  return await eventService
    .getCMEvent(data.cmEventId)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const updateCMEvent = createAsyncThunk('event/updateCMEvent', async (data, thunkAPI) => {
  return await eventService
    .updateCMEvent(data.cmEventId, data.eventId)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const finaliseSection = createAsyncThunk('event/finaliseSection', async (data, thunkAPI) => {
  return await eventService
    .finaliseSection(data.eventId, data.sectionId)
    .then((response) => {
      thunkAPI.dispatch(setAlertInfo('Section finalised successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertInfo(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const updateGreenSetup = createAsyncThunk(
  'event/updateGreenSetup',
  async (data, thunkAPI) => {
    return await eventService
      .greenSetup(data.eventId, data.locations)
      .then((response) => {
        thunkAPI.dispatch(setAlertInfo('Greens updated successfully'));
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        thunkAPI.dispatch(setAlertInfo(err.message));
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const splitSection = createAsyncThunk('event/splitSection', async (data, thunkAPI) => {
  return await eventService
    .splitSection(data.eventId, data.sectionId, data)
    .then((response) => {
      thunkAPI.dispatch(setAlertInfo('Section split successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertInfo(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const deleteSection = createAsyncThunk('event/deleteSection', async (data, thunkAPI) => {
  return await eventService
    .deleteSection(data.eventId, data.sectionId)
    .then((response) => {
      thunkAPI.dispatch(setAlertInfo('Section deleted successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      console.log(err.message);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const sectionDrawSetup = createAsyncThunk(
  'event/sectionDrawSetup',
  async (data, thunkAPI) => {
    return await eventService
      .sectionDrawSetup(data.eventId, data.sectionId, data.teams)
      .then((response) => {
        thunkAPI.dispatch(setAlertInfo('Section draw setup successfully'));
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        thunkAPI.dispatch(setAlertInfo(err.message));
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const generateSections = createAsyncThunk(
  'event/generateSections',
  async (data, thunkAPI) => {
    return await eventService
      .generateSections(data.eventId, data)
      .then((response) => {
        thunkAPI.dispatch(setAlertInfo('Sections generated successfully'));
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        thunkAPI.dispatch(setAlertInfo(err.message));
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const nextRound = createAsyncThunk('event/nextRound', async (data, thunkAPI) => {
  return await eventService
    .nextRound(data.eventId)
    .then((response) => {
      thunkAPI.dispatch(setAlertInfo('Advanced to next round successfully'));
      return response;
    })
    .catch((error) => {
      const err = error.response.data;
      thunkAPI.dispatch(setAlertInfo(err.message));
      return thunkAPI.rejectWithValue(err.message);
    });
});

export const getEventPlayerOpponentsByRound = createAsyncThunk(
  'event/getEventPlayerOpponentsByRound',
  async (data, thunkAPI) => {
    return await eventService
      .playerOpponents(data.eventId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

export const swapTeamsInGames = createAsyncThunk(
  'event/swapTeamsInGames',
  async (data, thunkAPI) => {
    return await eventService
      .swapTeamsInGames(data.eventId, data.teamA, data.teamB)
      .then((response) => {
        thunkAPI.dispatch(setAlertInfo('Teams swapped successfully'));
        return response;
      })
      .catch((error) => {
        const err = error.response.data;
        thunkAPI.dispatch(setAlertInfo(err.message));
        return thunkAPI.rejectWithValue(err.message);
      });
  }
);

// Event-wide changes answer with { success, code, message, details } on every
// failure, so the dialogs can show the exact reason (blocked conflict, stale
// preview, partial write). Transport failures fall back to the axios message.
const changeErrorPayload = (error) =>
  error?.response?.data && typeof error.response.data === 'object'
    ? error.response.data
    : { message: error?.message || 'The request could not be completed.' };

export const getEventVenueUsage = createAsyncThunk(
  'event/getEventVenueUsage',
  async (eventId, thunkAPI) => {
    try {
      return await eventService.getVenueUsage(eventId);
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const searchEventPlayers = createAsyncThunk(
  'event/searchEventPlayers',
  async ({ eventId, query, limit = 25 }, thunkAPI) => {
    try {
      return await eventService.searchPlayers(eventId, query, limit);
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const previewPlayerReplacement = createAsyncThunk(
  'event/previewPlayerReplacement',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.previewPlayerReplacement(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const applyPlayerReplacement = createAsyncThunk(
  'event/applyPlayerReplacement',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.applyPlayerReplacement(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const previewReservePromotion = createAsyncThunk(
  'event/previewReservePromotion',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.previewReservePromotion(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const applyReservePromotion = createAsyncThunk(
  'event/applyReservePromotion',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.applyReservePromotion(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const previewVenueSwap = createAsyncThunk(
  'event/previewVenueSwap',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.previewVenueSwap(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const applyVenueSwap = createAsyncThunk(
  'event/applyVenueSwap',
  async ({ eventId, payload }, thunkAPI) => {
    try {
      return await eventService.applyVenueSwap(eventId, payload);
    } catch (error) {
      const err = changeErrorPayload(error);
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getEventSnapshots = createAsyncThunk(
  'event/getEventSnapshots',
  async ({ eventId, limit = 50, offset = 0 }, thunkAPI) => {
    try {
      return await eventService.getEventSnapshots(eventId, { limit, offset });
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const saveEventSnapshot = createAsyncThunk(
  'event/saveEventSnapshot',
  async ({ eventId, name }, thunkAPI) => {
    try {
      const result = await eventService.saveEventSnapshot(eventId, { name });
      thunkAPI.dispatch(setAlertInfo('Restore point saved'));
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const deleteEventSnapshot = createAsyncThunk(
  'event/deleteEventSnapshot',
  async ({ eventId, snapshotId }, thunkAPI) => {
    try {
      const result = await eventService.deleteEventSnapshot(eventId, snapshotId);
      thunkAPI.dispatch(setAlertInfo('Restore point deleted'));
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const previewEventSnapshotRestore = createAsyncThunk(
  'event/previewEventSnapshotRestore',
  async ({ eventId, snapshotId }, thunkAPI) => {
    try {
      return await eventService.previewEventSnapshotRestore(eventId, snapshotId);
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const applyEventSnapshotRestore = createAsyncThunk(
  'event/applyEventSnapshotRestore',
  async ({ eventId, snapshotId, previewHash }, thunkAPI) => {
    try {
      return await eventService.applyEventSnapshotRestore(eventId, snapshotId, {
        previewHash
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(changeErrorPayload(error));
    }
  }
);

export const eventSlice = createSlice({
  name: 'events',
  initialState: {
    loading: true,
    event: null,
    events: [],
    // The events list loads page by page; `events` above stays the complete
    // list some older screens still read.
    eventPage: { events: [], total: 0, offset: 0, limit: 10, hasMore: false },
    eventPageLoading: false,
    eventPageScope: null,
    eventPageRequestId: null,
    ongoingEvents: [],
    ongoingEventsLoading: false,
    addGameLoading: false,
    finalisingSectionIds: [],
    splittingSection: false,
    clubmaster: {}
  },
  reducers: {
    setLoadingEvent: (state, action) => {
      state.loading = action.payload;
    },
    setEvent: (state, action) => {
      state.event = action.payload;
    },
    setEvents: (state, action) => {
      state.events = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllEvents.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
      })
      .addCase(getAllEvents.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getAllEvents.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getEventPage.pending, (state, action) => {
        state.eventPageLoading = true;
        state.eventPageScope = action.meta.arg?.scope || 'upcoming';
        state.eventPageRequestId = action.meta.requestId;
      })
      .addCase(getEventPage.fulfilled, (state, action) => {
        // Only the newest request may settle the page, so a slow response for
        // a page the user already left cannot replace what is on screen.
        if (state.eventPageRequestId !== action.meta.requestId) {
          return;
        }
        state.eventPage = action.payload;
        state.eventPageLoading = false;
      })
      .addCase(getEventPage.rejected, (state, action) => {
        if (state.eventPageRequestId !== action.meta.requestId) {
          return;
        }
        state.eventPageLoading = false;
      })
      .addCase(getOngoingEvents.pending, (state) => {
        state.ongoingEventsLoading = true;
      })
      .addCase(getOngoingEvents.fulfilled, (state, action) => {
        state.ongoingEvents = Array.isArray(action.payload?.events) ? action.payload.events : [];
        state.ongoingEventsLoading = false;
      })
      .addCase(getOngoingEvents.rejected, (state) => {
        state.ongoingEventsLoading = false;
      })
      .addCase(getAllEventsByVenue.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
      })
      .addCase(getAllEventsByVenue.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getAllEventsByVenue.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getEvent.fulfilled, (state, action) => {
        state.event = action.payload;
        state.loading = false;
      })
      .addCase(getEvent.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getEvent.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getEventScoreMethod.fulfilled, (state, action) => {
        if (state.event === null) {
          state.event = {};
        }

        state.event = action.payload;
        state.loading = false;
      })
      .addCase(getEventScoreMethod.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getEventScoreMethod.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
        state.saving = false;
      })
      .addCase(createEvent.pending, (state, action) => {
        state.saving = true;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.saving = false;
      })
      .addCase(addEventGame.fulfilled, (state, action) => {
        state.addGameLoading = false;
      })
      .addCase(addEventGame.pending, (state, action) => {
        state.addGameLoading = true;
      })
      .addCase(addEventGame.rejected, (state, action) => {
        state.addGameLoading = false;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        // state.event = action.payload;
        state.events = state.events.map((event) => {
          if (event.id === action.payload.id) {
            return action.payload;
          }
          return event;
        });
        state.saving = false;
      })
      .addCase(updateEvent.pending, (state, action) => {
        state.saving = true;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.saving = false;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter((event) => event.id !== action.payload.eventId);
        state.eventPage.events = state.eventPage.events.filter(
          (event) => event.id !== action.payload.eventId
        );
        state.eventPage.total = Math.max(0, state.eventPage.total - 1);
        state.loading = false;
      })
      .addCase(deleteEvent.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(updateEventTeam.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.event = action.payload;
          state.events = state.events.map((event) =>
            event.id === action.payload.id ? action.payload : event
          );
        }
        state.updatingTeam = false;
      })
      .addCase(updateEventTeam.pending, (state, action) => {
        state.updatingTeam = true;
      })
      .addCase(updateEventTeam.rejected, (state, action) => {
        state.updatingTeam = false;
      })
      .addCase(getCMEvents.fulfilled, (state, action) => {
        const convertedEvents = [];
        for (let i = 0; i < action.payload.length; i++) {
          const item = action.payload[i];
          let newItem = item;
          const newDate = convertFirebaseDate(item.startDate);
          newItem.startDate = newDate;
          convertedEvents.push(newItem);
        }

        convertedEvents.sort((a, b) => {
          return a.startDate - b.startDate;
        });

        state.clubmaster.events = convertedEvents;
        state.clubmaster.loadingEvents = false;
      })
      .addCase(getCMEvents.pending, (state, action) => {
        state.clubmaster.loadingEvents = true;
      })
      .addCase(getCMEvents.rejected, (state, action) => {
        state.clubmaster.loadingEvents = false;
      })
      .addCase(getCMEvent.fulfilled, (state, action) => {
        state.clubmaster.event = action.payload;
        state.clubmaster.loadingEvent = false;
      })
      .addCase(getCMEvent.pending, (state, action) => {
        state.clubmaster.loadingEvent = true;
      })
      .addCase(getCMEvent.rejected, (state, action) => {
        state.clubmaster.loadingEvent = false;
      })
      .addCase(updateCMEvent.fulfilled, (state, action) => {
        console.log(action.payload);
        state.clubmaster.updatingEvent = false;
      })
      .addCase(updateCMEvent.pending, (state, action) => {
        state.clubmaster.updatingEvent = true;
      })
      .addCase(updateCMEvent.rejected, (state, action) => {
        state.clubmaster.updatingEvent = false;
      })
      .addCase(finaliseSection.fulfilled, (state, action) => {
        // Older API builds only answered { message, success }, so fall back to
        // the dispatched section id and leave the fields we did not receive.
        const sectionId = action.payload?.sectionId || action.meta.arg?.sectionId;
        state.finalisingSectionIds = state.finalisingSectionIds.filter(
          (id) => id !== sectionId
        );

        state.event.sections = state.event.sections.map((section) => {
          if (section.id === sectionId) {
            if (action.payload?.finalised) {
              section.finalised = action.payload.finalised;
            }
            if (action.payload?.winner) {
              section.winner = action.payload.winner;
            }
            if (action.payload?.winReason) {
              section.winReason = action.payload.winReason;
            }
          }
          return section;
        });
      })
      .addCase(finaliseSection.pending, (state, action) => {
        const sectionId = action.meta.arg?.sectionId;
        if (sectionId && !state.finalisingSectionIds.includes(sectionId)) {
          state.finalisingSectionIds.push(sectionId);
        }
      })
      .addCase(finaliseSection.rejected, (state, action) => {
        const sectionId = action.meta.arg?.sectionId;
        state.finalisingSectionIds = state.finalisingSectionIds.filter(
          (id) => id !== sectionId
        );
      })
      .addCase(splitSection.fulfilled, (state, action) => {
        state.event.sections = action.payload.sections;
        state.splittingSection = false;
      })
      .addCase(splitSection.pending, (state, action) => {
        state.splittingSection = true;
      })
      .addCase(splitSection.rejected, (state, action) => {
        state.splittingSection = false;
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.event.sections = state.event.sections.filter(
          (section) => section.id !== action.payload.sectionId
        );
        state.deletingSection = false;
      })
      .addCase(deleteSection.pending, (state, action) => {
        state.deletingSection = true;
      })
      .addCase(deleteSection.rejected, (state, action) => {
        state.deletingSection = false;
      })
      .addCase(updateGreenSetup.fulfilled, (state, action) => {
        state.events = state.events.map((event) => {
          if (event.id === action.payload.id) {
            return { ...event, greens: action.payload.greens };
          }
          return event;
        });

        state.loading = false;
      })
      .addCase(updateGreenSetup.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateGreenSetup.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(sectionDrawSetup.fulfilled, (state, action) => {
        const section = state.event.sections.find((sec) => sec.id === action.payload.sectionId);
        if (section) {
          section.initialDraw = action.payload.teams;
        }
        state.loadingDraw = false;
      })
      .addCase(sectionDrawSetup.pending, (state, action) => {
        state.loadingDraw = true;
      })
      .addCase(sectionDrawSetup.rejected, (state, action) => {
        state.loadingDraw = false;
      })
      .addCase(generateSections.fulfilled, (state, action) => {
        state.event.sections = action.payload.sections;
        state.saving = false;
      })
      .addCase(generateSections.pending, (state, action) => {
        state.saving = true;
      })
      .addCase(generateSections.rejected, (state, action) => {
        state.saving = false;
      })
      .addCase(nextRound.fulfilled, (state, action) => {
        state.event.currentRound = action.payload.currentRound;
        state.loading = false;
      })
      .addCase(nextRound.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(nextRound.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getEventPlayerOpponentsByRound.fulfilled, (state, action) => {
        state.playerOpponents = action.payload;
        state.loadingPlayerOpponents = false;
      })
      .addCase(getEventPlayerOpponentsByRound.pending, (state, action) => {
        state.loadingPlayerOpponents = true;
      })
      .addCase(getEventPlayerOpponentsByRound.rejected, (state, action) => {
        state.loadingPlayerOpponents = false;
      })
      .addCase(swapTeamsInGames.fulfilled, (state, action) => {
        if (action.payload.swappedTeams && state.playerOpponents) {
          Object.keys(action.payload.swappedTeams).forEach((teamId) => {
            state.playerOpponents[teamId] = action.payload.swappedTeams[teamId];
          });
        }

        state.swappingTeams = false;
      })
      .addCase(swapTeamsInGames.pending, (state, action) => {
        state.swappingTeams = true;
      })
      .addCase(swapTeamsInGames.rejected, (state, action) => {
        state.swappingTeams = false;
      });
  }
});

export const { setEvent, setLoadingEvent } = eventSlice.actions;

export default eventSlice.reducer;
